import { ReactNode } from "react";
import { Button, alpha } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";

interface Props {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** No es un ajuste de diseño: la convención del repo pide `data-testid` en todo control. */
  "data-testid": string;
}

/**
 * CTA de un proceso guiado del expediente: la pastilla del `.proc-row` del diseño
 * (`ProcessCard` en `expediente-screens.jsx`) — color primario al 12% de fondo, su borde al 35%,
 * texto en primario y la flecha al final.
 *
 * Solo recibe el texto, el `onClick` y si va deshabilitado: la forma es fija a propósito, para que
 * las franjas de proceso se vean iguales en todas las pantallas del expediente.
 */
export const ButtonStartExpediente = ({ children, onClick, disabled, ...rest }: Props) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    disableElevation
    endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
    sx={(theme) => ({
      flexShrink: 0,
      whiteSpace: "nowrap",
      textTransform: "none",
      borderRadius: "99px",
      p: "8px 16px",
      fontSize: 13,
      fontWeight: 600,
      color: "primary.main",
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      border: `1.5px solid ${alpha(theme.palette.primary.main, 0.35)}`,
      transition: "all .12s",
      // El diseño declara la transición pero no el estado hover; se sube un escalón cada alfa.
      "&:hover": {
        bgcolor: alpha(theme.palette.primary.main, 0.2),
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
      /*
       * Hay que declararlo: el `color` y el `bgcolor` de arriba van sin condición, así que pisan
       * el estilo que MUI aplica solo. Se apaga la pastilla completa —texto, borde y flecha— con
       * la misma opacidad que usa el `Button` del kit.
       */
      "&.Mui-disabled": {
        color: "primary.main",
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        borderColor: alpha(theme.palette.primary.main, 0.35),
        opacity: 0.45,
      },
      "& .MuiButton-endIcon": { ml: "7px" },
    })}
    {...rest}
  >
    {children}
  </Button>
);
